import { useClearableReducer } from "@client-shared/hooks/use-clearable-reducer";
import {
  DEFAULT_QUERY_LIMIT,
  DEFAULT_QUERY_MAX,
  ORDERS_FIRST_PAGE_LIMIT,
  ORDERS_PAGE_LIMIT,
  ROUTE_PATHS,
} from "@client-shared/lib/constants";
import i18n from "@client-shared/lib/i18n";
import { isSameObject } from "@client-shared/lib/util";
import { useSFDCAuthState } from "@client-shared/providers/auth";
import { useVendorContext } from "@client-shared/providers/vendor";
import * as api from "@client-shared/services/api";
import { handleDisplayErrorData } from "@client-shared/services/api/utilities/handleDisplayErrorData";
import { paginate } from "@shared/lib/concurrency/paginate";
import type * as T from "@shared/types";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { type DataTableRow } from "../../types/data-table.types";
import {
  reducer,
  setFetchEnabled as setFetchEnabledAction,
  setFetching,
  setFullyLoaded,
  setInitialized,
  setLoading,
  setLoadingKeys,
  setLoadingProgress,
  setPagination,
  setParams,
  setPurchaseOrder,
  setPurchaseOrderId,
  setPurchaseOrders,
  setPurchaseOrdersCount,
  setShippingDocumentsDialog,
  setSuccess,
} from "./actions";
import { ErrorResponse, PurchaseOrdersActions, PurchaseOrdersContextState, PurchaseOrdersState, UsePurchaseOrdersUtils } from "./types";

// Common error handling helper
const handleCommonError = (error: unknown, messageKey: string, titleKey: string) => {
  handleDisplayErrorData(i18n.t(messageKey), i18n.t(titleKey), error as Error);
};

const initialState = {
  purchaseOrders: [],
  purchaseOrderId: undefined,
  purchaseOrder: undefined,
  batchId: undefined,
  isInitialized: false,
  isLoading: false,
  errors: undefined,
  params: undefined,
  limit: DEFAULT_QUERY_LIMIT,
  max: DEFAULT_QUERY_MAX,
  pagination: {
    pageSize: 50,
    pageIndex: 0,
  },
  loadingProgress: null,
  isFullyLoaded: false,
};

export const PurchaseOrdersContext = React.createContext<PurchaseOrdersContextState>([
  initialState,
  () => {},
  {} as UsePurchaseOrdersUtils,
]);

// Query key prefix — exported so other providers (e.g. BatchesProvider) can invalidate without hardcoding
export const PURCHASE_ORDERS_QUERY_KEY = "purchaseOrders" as const;
export const PURCHASE_ORDER_QUERY_KEY = "purchaseOrder" as const;

// Query key factory
const queryKeys = {
  purchaseOrders: (params?: T.FetchOrdersRequest) => [PURCHASE_ORDERS_QUERY_KEY, params],
  purchaseOrder: (params?: T.FetchPurchaseOrderRequest) => [PURCHASE_ORDER_QUERY_KEY, params],
};

export const PurchaseOrdersProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const queryClient = useQueryClient();
  const [state, dispatch, clearState] = useClearableReducer(reducer, initialState);
  const { t } = useTranslation();

  const [authState] = useSFDCAuthState();
  const [{ initialized: isVendorInitialized, currentVendor }] = useVendorContext();

  const [searchParams, setSearchParams] = useSearchParams();

  // Get current location to check pathname
  const location = useLocation();
  const isOrdersPath = React.useMemo(() => location.pathname.includes(ROUTE_PATHS.ORDERS), [location.pathname]);
  const isBatchesPath = React.useMemo(() => location.pathname.includes(ROUTE_PATHS.BATCHES), [location.pathname]);

  // Fetch purchase orders
  const { data, isLoading, error, isSuccess, isFetching } = useQuery({
    queryKey: queryKeys.purchaseOrders(state.params),
    queryFn: ({ queryKey, signal }) => {
      const [, params] = queryKey as [string, T.FetchOrdersRequest];
      if (params && typeof params === "object") {
        return fetchPurchaseOrders(params, signal);
      }
    },
    enabled: state.isInitialized && state.isFetchEnabled !== false && Boolean(currentVendor?.vendorKey),
  });

  const clearCache = React.useCallback(() => {
    if (isBatchesPath) {
      void queryClient.refetchQueries({ queryKey: ["batches"] });
    }
    const purchaseOrdersKey = queryKeys.purchaseOrders(state.params);
    queryClient.removeQueries({ queryKey: purchaseOrdersKey });
    void queryClient.invalidateQueries({ queryKey: purchaseOrdersKey });

    if (currentVendor && state.purchaseOrderId) {
      const purchaseOrderKey = queryKeys.purchaseOrder({
        vendorKey: currentVendor.vendorKey,
        purchaseOrderId: state.purchaseOrderId,
      });
      queryClient.removeQueries({ queryKey: purchaseOrderKey });
      void queryClient.invalidateQueries({ queryKey: purchaseOrderKey });
    }
  }, [queryClient, state.params, state.purchaseOrderId, currentVendor, isBatchesPath]);

  const fetchPurchaseOrders = React.useCallback(
    async (newParams?: T.FetchOrdersRequest, signal?: AbortSignal) => {
      const params = newParams ?? state.params;
      if (!params) {
        return;
      }
      dispatch(setFullyLoaded(false));
      dispatch(setLoadingProgress(null));
      dispatch(setPurchaseOrders([]));
      const transformedOrders: DataTableRow<T.PurchaseOrder>[] = [];
      const result = await fetchAllPurchaseOrders(params, undefined, signal, (partialOrders, progress) => {
        if (signal?.aborted) {
          return;
        }
        const newOrders = partialOrders.slice(transformedOrders.length);
        const newlyTransformed = newOrders.map((order: T.PurchaseOrder) => transformToOrderRowState(order));
        transformedOrders.push(...newlyTransformed);
        dispatch(setPurchaseOrders([...transformedOrders]));
        dispatch(setLoadingProgress(progress));
      });
      if (!signal?.aborted) {
        dispatch(setLoadingProgress(null));
        dispatch(setFullyLoaded(result.complete));
      }
      return { orders: result.orders };
    },
    [state.params, dispatch]
  );

  const clearPurchaseOrder = React.useCallback(() => {
    dispatch(setPurchaseOrder());
    dispatch(setPurchaseOrderId());
  }, [dispatch]);

  const fetchPurchaseOrder = React.useCallback(
    async (purchaseOrderId: string, reload?: boolean) => {
      if (!currentVendor) {
        return;
      }
      const existingOrder = state.purchaseOrders?.find((order: DataTableRow<T.PurchaseOrder>) => order.purchaseOrderId === purchaseOrderId);
      if (existingOrder && !reload) {
        dispatch(setPurchaseOrder(existingOrder));
        return;
      }
      await fetchPurchaseOrderWithErrorHandling(purchaseOrderId, currentVendor.vendorKey, queryClient, dispatch);
    },
    [state.purchaseOrders, queryClient, currentVendor, dispatch]
  );

  const getPurchaseOrdersCount = React.useCallback(
    async (params?: T.FetchOrdersRequest): Promise<{ key: string; value: number }> => {
      if (!params || !currentVendor) {
        return { key: "", value: 0 };
      }
      const key = generateParamsKey(JSON.stringify(params));
      const orders = await fetchPurchaseOrdersWithParams(params, queryClient);
      dispatch(
        setPurchaseOrdersCount({
          ...state.count,
          [key]: orders?.length ?? 0,
        })
      );
      return { key, value: orders?.length ?? 0 };
    },
    [currentVendor, queryClient, state.count, dispatch]
  );

  // Common loading state management
  React.useEffect(() => {
    dispatch(setLoading(isLoading));
  }, [isLoading, dispatch]);

  React.useEffect(() => {
    dispatch(setSuccess(isSuccess));
  }, [isSuccess, dispatch]);

  React.useEffect(() => {
    dispatch(setFetching(isFetching));
  }, [isFetching, dispatch]);

  // Get individual purchase order when purchaseOrderId is set
  React.useEffect(() => {
    if (state.purchaseOrders && state.purchaseOrders.length > 0 && state.purchaseOrderId && !state.isLoading) {
      void fetchPurchaseOrder(state.purchaseOrderId);
    }
  }, [state.isLoading, state.purchaseOrders, state.purchaseOrderId, fetchPurchaseOrder]);

  // Sync query result to state — skipped when progressive loading already dispatched the data
  React.useEffect(() => {
    if (data?.orders && !state.isFullyLoaded) {
      const orders = data.orders.map((order: T.PurchaseOrder) => transformToOrderRowState(order));
      if (orders.length !== state.purchaseOrders?.length) {
        dispatch(setPurchaseOrders(orders));
      }
    }
  }, [data?.orders, dispatch, state.purchaseOrders, state.isFullyLoaded]);

  // Common error handling
  React.useEffect(() => {
    if (error) {
      handleDisplayErrorData(t("apiErrorMessages.fetchPurchaseOrders.message"), t("apiErrorMessages.fetchPurchaseOrders.title"), error);
    }
  }, [error, t]);

  React.useEffect(() => {
    if (!isOrdersPath) {
      return;
    }
    const orderStatus = searchParams.get("orderStatus");
    if (!orderStatus && state.params?.orderStatus) {
      return setSearchParams((prev) => ({
        ...Object.fromEntries(prev.entries()),
        orderStatus: state.params?.orderStatus?.join(",") ?? "",
      }));
    }
    const orderStatusArray = orderStatus ? (orderStatus.split(",") as T.PurchaseOrderStatus[]) : [];
    if (!state.params || isSameObject(state.params.orderStatus, orderStatusArray)) {
      return;
    }
    dispatch(
      setParams({
        ...state.params,
        orderStatus: orderStatusArray,
      })
    );
    // Only re-run when the URL param or the params object itself changes — not on every state/render.
  }, [searchParams, state.params, setSearchParams, dispatch, isOrdersPath]);

  // Reset order list and fully-loaded flag when filter params change.
  React.useEffect(() => {
    dispatch(setFullyLoaded(false));
    dispatch(setLoadingProgress(null));
    dispatch(setPurchaseOrders([]));
  }, [state.params, dispatch]);

  const initialize = React.useCallback<UsePurchaseOrdersUtils["initialize"]>(
    (state) => {
      if (!currentVendor) {
        return;
      }
      const { pagination, params, purchaseOrderId } = state;
      const newParams = {
        ...params,
        sortByCreatedAt: params?.sortByCreatedAt ?? "desc",
        limit: params?.limit ?? DEFAULT_QUERY_LIMIT,
        vendorKey: currentVendor.vendorKey,
      };
      if (!isSameObject(state.pagination, pagination)) {
        dispatch(
          setPagination({
            ...pagination,
            pageIndex: pagination?.pageIndex ?? 0,
            pageSize: pagination?.pageSize ?? 20,
          })
        );
      }
      if (!isSameObject(state.params, newParams)) {
        dispatch(setParams(newParams));
      }
      if (purchaseOrderId) {
        dispatch(setPurchaseOrderId(purchaseOrderId));
      }
      if (isOrdersPath && isVendorInitialized && authState && !state.isInitialized) {
        dispatch(setInitialized(true));
      }
    },
    // intentionally NOT including state.params or state.pagination
    // to keep this callback stable and prevent infinite loops in consumers that call initialize in useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentVendor, authState, isOrdersPath, isVendorInitialized, state.isInitialized, dispatch]
  );

  const getPurchaseOrderShippingDocument = React.useCallback(
    (purchaseOrder: T.PurchaseOrder, fileType?: T.LabelPrintBatchFileType) =>
      fetchPurchaseOrderShippingDocumentHandler(purchaseOrder, dispatch, state, clearCache, currentVendor, fileType),
    [currentVendor, state, dispatch, clearCache]
  );
  const createPurchaseOrderShippingDocuments = React.useCallback(
    (purchaseOrder: T.PurchaseOrder) =>
      createPurchaseOrderShippingDocumentsHandler(purchaseOrder, dispatch, state, clearCache, currentVendor),
    [currentVendor, state, dispatch, clearCache]
  );

  const getPurchaseOrderShippingDocumentUrl = React.useCallback(
    (purchaseOrder: T.PurchaseOrder, fileType?: T.LabelPrintBatchFileType) =>
      fetchPurchaseOrderShippingDocumentUrlHandler(purchaseOrder, dispatch, state, currentVendor, fileType),
    [currentVendor, state, dispatch]
  );

  const setFetchEnabled = React.useCallback<UsePurchaseOrdersUtils["setFetchEnabled"]>(
    (enabled) => dispatch(setFetchEnabledAction(enabled)),
    [dispatch]
  );

  const usePurchaseOrdersUtils: UsePurchaseOrdersUtils = React.useMemo(() => {
    return {
      clearState,
      fetchPurchaseOrders,
      fetchPurchaseOrder,
      clearPurchaseOrder,
      clearCache,
      getPurchaseOrdersCount,
      getPurchaseOrderShippingDocument,
      getPurchaseOrderShippingDocumentUrl,
      createPurchaseOrderShippingDocuments,
      setParams,
      setFetchEnabled,
      initialize,
    };
  }, [
    clearState,
    fetchPurchaseOrders,
    fetchPurchaseOrder,
    initialize,
    clearCache,
    clearPurchaseOrder,
    getPurchaseOrdersCount,
    getPurchaseOrderShippingDocument,
    getPurchaseOrderShippingDocumentUrl,
    createPurchaseOrderShippingDocuments,
    setFetchEnabled,
  ]);

  const contextValue: PurchaseOrdersContextState = React.useMemo(
    () => [state, dispatch, usePurchaseOrdersUtils],
    [state, usePurchaseOrdersUtils, dispatch]
  );

  return <PurchaseOrdersContext.Provider value={contextValue}>{children}</PurchaseOrdersContext.Provider>;
};

// utilities
export const transformToOrderRowState = (row: T.PurchaseOrder): DataTableRow<T.PurchaseOrder> => {
  // Existing function implementation stays the same
  const { orderStatus, batchId, beanzConnectLabelPrintEnabled } = row;
  let tone: DataTableRow<T.PurchaseOrder>["tone"];
  const errors: Error[] = [];
  const isLoading = Boolean(orderStatus === "WaitingToBeFulfilled" && batchId);
  const isDisabled = !beanzConnectLabelPrintEnabled;
  const errorStatus: T.PurchaseOrderStatus[] = ["Error", "LabelError", "ZenkraftAddressError", "DeliveryError"];
  const hasError = orderStatus && errorStatus.includes(orderStatus);
  if (hasError) {
    tone = "critical";
    errors.push({ name: "Error", message: "Label error" });
  }
  if (orderStatus === "Canceled") {
    tone = "canceled";
  }
  if (orderStatus === "AddressValidationError") {
    tone = "subdued";
  }
  return {
    ...row,
    isDisabled,
    isLoading,
    hasError,
    tone,
    errors,
  };
};

export function generateParamsKey(params: string): string {
  const intermediate = params
    .toLowerCase()
    .replaceAll(/\s+/g, "-")
    .replaceAll(/[^\w-]+/g, "");

  // Remove leading hyphens (linear time, no backtracking)
  let start = 0;
  while (start < intermediate.length && intermediate[start] === "-") {
    start++;
  }

  // Remove trailing hyphens (linear time, no backtracking)
  let end = intermediate.length;
  while (end > start && intermediate[end - 1] === "-") {
    end--;
  }

  return intermediate.slice(start, end);
}
// sideEffects
async function fetchPurchaseOrderWithErrorHandling(
  purchaseOrderId: string,
  vendorKey: string,
  queryClient: QueryClient,
  dispatch: React.Dispatch<PurchaseOrdersActions>
): Promise<void> {
  try {
    const order = await queryClient.fetchQuery({
      queryKey: queryKeys.purchaseOrder({ purchaseOrderId, vendorKey }),
      queryFn: async () => {
        const { data } = await api.getPurchaseOrderById({
          purchaseOrderId,
          vendorKey,
        });
        return data ? transformToOrderRowState(data) : null;
      },
    });
    if (order) {
      dispatch(setPurchaseOrder(order));
    }
  } catch (e) {
    handleCommonError(e, "apiErrorMessages.fetchPurchaseOrder.message", "apiErrorMessages.fetchPurchaseOrder.title");
  }
}

export interface BatchProgress {
  loaded: number;
  estimated: number;
}

export interface FetchAllResult {
  orders: T.PurchaseOrder[];
  complete: boolean;
}

/** Pause between pages with abort signal support */
function abortAwarePause(pauseMs: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (!signal) {
      setTimeout(resolve, pauseMs);
      return;
    }
    if (signal.aborted) {
      resolve();
      return;
    }
    const cleanup = (): void => signal.removeEventListener("abort", onAbort);
    function onAbort(): void {
      clearTimeout(timer);
      cleanup();
      resolve();
    }
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, pauseMs);
    signal.addEventListener("abort", onAbort);
  });
}

/** Fetch a single page, returning the data or null if aborted/errored with partial results */
async function fetchPage(
  params: T.FetchOrdersRequest,
  signal?: AbortSignal,
  existingCount?: number
): Promise<{ data: T.PurchaseOrderResponse["data"]; shouldStop: false } | { shouldStop: true }> {
  try {
    const { data } = await api.getPurchaseOrders(params, signal);
    return { data, shouldStop: false };
  } catch (error: unknown) {
    if (signal?.aborted) {
      return { shouldStop: true };
    }
    if (!existingCount) {
      throw error;
    }
    console.warn(
      `Error fetching batch after ${existingCount} orders loaded. Returning partial results: ${error instanceof Error ? error.message : "Unknown error"}`
    );
    return { shouldStop: true };
  }
}

/** Process a batch result: push orders, check limits, notify callback. Returns whether fetching is complete. */
function processBatch(
  allOrders: T.PurchaseOrder[],
  batchOrders: T.PurchaseOrder[],
  seenIds: Set<string>,
  currentLimit: number,
  max: number,
  onBatchReceived?: (orders: T.PurchaseOrder[], progress: BatchProgress) => void
): { complete: boolean; done: boolean } {
  // Deduplicate by purchaseOrderId across all pages — the multi-status "All" filter
  // can return the same PO from cursor-boundary repeats or overlapping status matches.
  const uniqueBatch = batchOrders.filter((order) => {
    if (seenIds.has(order.purchaseOrderId)) return false;
    seenIds.add(order.purchaseOrderId);
    return true;
  });
  allOrders.push(...uniqueBatch);
  if (allOrders.length >= max) {
    allOrders.length = max;
    onBatchReceived?.([...allOrders], { loaded: max, estimated: max });
    console.warn(`Reached maximum limit of ${max} results. Stopping further fetch.`);
    return { complete: true, done: true };
  }
  // isLastBatch is determined by what the backend returned, not by post-dedupe size,
  // so a fully-duplicate page still correctly terminates pagination.
  const isLastBatch = batchOrders.length < currentLimit;
  onBatchReceived?.([...allOrders], {
    loaded: allOrders.length,
    estimated: isLastBatch ? allOrders.length : max,
  });
  return { complete: isLastBatch, done: isLastBatch };
}

export async function fetchAllPurchaseOrders(
  initialParams: T.FetchOrdersRequest,
  pauseMs: number = 50,
  signal?: AbortSignal,
  onBatchReceived?: (orders: T.PurchaseOrder[], progress: BatchProgress) => void
): Promise<FetchAllResult> {
  const allOrders: T.PurchaseOrder[] = [];
  const seenIds = new Set<string>();
  const max = initialParams.max ?? DEFAULT_QUERY_MAX;
  const fullLimit = Math.min(initialParams.limit, max, ORDERS_PAGE_LIMIT);
  let complete = false;
  const firstPageParams = {
    ...initialParams,
    limit: Math.min(ORDERS_FIRST_PAGE_LIMIT, fullLimit),
  };
  // Cursor pagination: each request is addressed by the previous page's last purchase order id and is only made
  // while the signal has not been aborted (an aborted signal yields `undefined` and ends the loop).
  const pages = paginate(async (nextPageParams: typeof firstPageParams | undefined) => {
    if (signal?.aborted) {
      return { page: undefined, nextCursor: undefined };
    }
    const params = nextPageParams ?? firstPageParams;
    const result = await fetchPage(params, signal, allOrders.length);
    const nextCursor = result.shouldStop ? undefined : result.data?.lastPurchaseOrder?.purchaseOrderId;
    return {
      page: { result, params },
      nextCursor: nextCursor ? { ...params, limit: fullLimit, lastPurchaseOrderId: nextCursor } : undefined,
    };
  });
  for await (const page of pages) {
    if (!page) {
      break;
    }
    const { result, params } = page;
    if (result.shouldStop) {
      break;
    }
    if (!result.data?.orders?.length) {
      complete = true;
      break;
    }
    const batch = processBatch(allOrders, result.data.orders, seenIds, params.limit, max, onBatchReceived);
    if (batch.done) {
      complete = batch.complete;
      break;
    }
    if (!result.data.lastPurchaseOrder?.purchaseOrderId) {
      complete = true;
      break;
    }
    // Pause between cursor-paginated pages to respect rate limits.
    await abortAwarePause(pauseMs, signal);
  }

  return { orders: allOrders, complete };
}

async function fetchPurchaseOrdersWithParams(
  params: T.FetchOrdersRequest,
  queryClient: QueryClient
): Promise<T.PurchaseOrder[] | undefined> {
  const queryKey = queryKeys.purchaseOrders(params);
  try {
    const cached = queryClient.getQueryData<{ orders: T.PurchaseOrder[] } | undefined>(queryKey);
    if (cached?.orders) {
      return cached.orders;
    }
    const fetched = await queryClient.fetchQuery({
      queryKey,
      queryFn: async () => {
        const result = await fetchAllPurchaseOrders(params);
        return { orders: result.orders };
      },
    });
    return fetched?.orders;
  } catch (error) {
    handleCommonError(error, "apiErrorMessages.fetchPurchaseOrders.message", "apiErrorMessages.fetchPurchaseOrders.title");
  }
}

async function createPurchaseOrderShippingDocumentsHandler(
  purchaseOrder: T.PurchaseOrder,
  dispatch: React.Dispatch<PurchaseOrdersActions>,
  state: PurchaseOrdersState,
  clearCache: UsePurchaseOrdersUtils["clearCache"],
  currentVendor?: T.Vendor
) {
  if (!currentVendor || !purchaseOrder) {
    throw new Error("Current vendor or PO is not defined");
  }

  const loadingKey = "createPurchaseOrderShippingDocuments";
  dispatch(setLoadingKeys({ ...state.loadingKeys, [loadingKey]: true }));

  const { purchaseOrderId, batchId } = purchaseOrder;

  try {
    const { data } = await api.createPurchaseOrderShippingDocuments({
      purchaseOrderId,
      batchId,
      vendorKey: purchaseOrder.vendorKey,
    });
    if (data) {
      toast.success(i18n.t("components.PurchaseOrder.creatingShippingDocuments"));
    }
  } catch (error) {
    handleCommonError(error, "apiErrorMessages.createShippingDocuments.message", "apiErrorMessages.createShippingDocuments.title");
  } finally {
    dispatch(setLoadingKeys({ ...state.loadingKeys, [loadingKey]: false }));
    clearCache();
  }
}

// Helper function to get shipping document
const getShippingDocument = async (purchaseOrder: T.PurchaseOrder, fileType: T.LabelPrintBatchFileType) => {
  const { purchaseOrderId, batchId } = purchaseOrder;

  const { data } = await api.getPurchaseOrderShippingDocument({
    purchaseOrderId,
    batchId,
    vendorKey: purchaseOrder.vendorKey,
    type: fileType,
  });

  if (!data || typeof data === "object") {
    throw new Error("error fetching shipping document");
  }

  return data;
};

// Helper function to create shipping documents
const createShippingDocuments = async (purchaseOrder: T.PurchaseOrder) => {
  const { purchaseOrderId, batchId } = purchaseOrder;

  const { data } = await api.createPurchaseOrderShippingDocuments({
    purchaseOrderId,
    batchId,
    vendorKey: purchaseOrder.vendorKey,
  });

  if (!data) {
    throw new Error("Failed to create shipping documents");
  }

  toast.success(i18n.t("components.PurchaseOrder.creatingShippingDocuments"));
  return data;
};

async function fetchPurchaseOrderShippingDocumentHandler(
  purchaseOrder: T.PurchaseOrder,
  dispatch: React.Dispatch<PurchaseOrdersActions>,
  state: PurchaseOrdersState,
  clearCache: UsePurchaseOrdersUtils["clearCache"],
  currentVendor?: T.Vendor,
  fileType: T.LabelPrintBatchFileType = "Label"
) {
  if (!currentVendor || !purchaseOrder) {
    throw new Error("Current vendor or PO is not defined");
  }

  const loadingKeyMap: Partial<Record<T.LabelPrintBatchFileType, string>> = {
    PackingSlip: "getPurchaseOrderPackingSlip",
    Label: "getPurchaseOrderLabel",
  };
  const loadingKey = loadingKeyMap[fileType] ?? "getPurchaseOrderLabel";

  dispatch(setLoadingKeys({ ...state.loadingKeys, [loadingKey]: true }));

  try {
    // Try to get the document first
    await getShippingDocument(purchaseOrder, fileType);
    // Instead of opening a blank window, open the ShippingDocumentsDialog
    dispatch(setShippingDocumentsDialog(true, purchaseOrder, undefined, fileType === "PackingSlip" ? "packing-slip" : "shipping-label"));
  } catch {
    try {
      // If getting fails, create the documents and retry
      await createShippingDocuments(purchaseOrder);
      await retryGetDocument(purchaseOrder, fileType, clearCache);
    } catch (retryError) {
      handleDisplayErrorData(
        i18n.t("apiErrorMessages.createShippingDocuments.message"),
        i18n.t("apiErrorMessages.createShippingDocuments.title"),
        (retryError as ErrorResponse).response?.data
      );
    }
  } finally {
    dispatch(setLoadingKeys({ ...state.loadingKeys, [loadingKey]: false }));
    clearCache();
  }
}

const fetchPurchaseOrderShippingDocumentUrlHandler = async (
  purchaseOrder: T.PurchaseOrder,
  dispatch: React.Dispatch<PurchaseOrdersActions>,
  state: PurchaseOrdersState,
  currentVendor: T.Vendor | undefined,
  fileType: T.LabelPrintBatchFileType = "Label"
): Promise<string | null> => {
  if (!currentVendor) {
    throw new Error("No vendor selected");
  }

  const loadingKeyMap: Partial<Record<T.LabelPrintBatchFileType, string>> = {
    PackingSlip: "getPurchaseOrderPackingSlipUrl",
    Label: "getPurchaseOrderLabelUrl",
  };
  const loadingKey = loadingKeyMap[fileType] ?? "getPurchaseOrderLabelUrl";

  dispatch(setLoadingKeys({ ...state.loadingKeys, [loadingKey]: true }));

  try {
    // Try to get the document first
    return await getShippingDocument(purchaseOrder, fileType);
  } catch {
    try {
      // If getting fails, create the documents and retry
      await createShippingDocuments(purchaseOrder);
      // Retry after creating documents
      return await getShippingDocument(purchaseOrder, fileType);
    } catch (error) {
      const errorResponse = (error as ErrorResponse).response?.data;
      handleDisplayErrorData(
        i18n.t("apiErrorMessages.fetchShippingDocument.message"),
        i18n.t("apiErrorMessages.fetchShippingDocument.title"),
        errorResponse
      );
    }
    return null;
  } finally {
    dispatch(setLoadingKeys({ ...state.loadingKeys, [loadingKey]: false }));
  }
};

async function retryGetDocument(
  purchaseOrder: T.PurchaseOrder,
  fileType: T.LabelPrintBatchFileType,
  clearCache: UsePurchaseOrdersUtils["clearCache"],
  retryDelayMs: number = 3000
) {
  const maxRetries = 3;
  const attemptToGetDocument = async (): Promise<boolean> => {
    try {
      const { data } = await api.getPurchaseOrderShippingDocument({
        purchaseOrderId: purchaseOrder.purchaseOrderId,
        vendorKey: purchaseOrder.vendorKey,
        type: fileType,
      });
      if (data) {
        window.open(data, "_blank");
        return true;
      }
    } catch (error) {
      console.log(error);
    }
    return false;
  };
  await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
  try {
    if (await attemptToGetDocument()) {
      clearCache();
      return;
    }
    for (let retryCount = 0; retryCount < maxRetries - 1; retryCount++) {
      await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
      try {
        if (await attemptToGetDocument()) {
          clearCache();
          return;
        }
      } catch (error) {
        if (retryCount === maxRetries - 2) {
          handleDisplayErrorData(
            i18n.t("apiErrorMessages.fetchShippingDocument.message"),
            i18n.t("apiErrorMessages.fetchShippingDocument.title"),
            error as Error
          );
        }
      }
    }
  } catch (error) {
    handleDisplayErrorData(i18n.t("apiErrorMessages.fetchBatch.message"), i18n.t("apiErrorMessages.fetchBatch.title"), error as Error);
  } finally {
    clearCache();
  }
}
